import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Persona } from '../types/persona';
import { CategoryTabs } from '../components/personas/CategoryTabs';
import { TagFilter } from '../components/personas/TagFilter';
import { PersonaGrid } from '../components/personas/PersonaGrid';
import { PersonaDetail } from '../components/personas/PersonaDetail';
import { TagCloud } from '../components/personas/TagCloud';
import * as personaService from '../services/personaService';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Spinner } from '../components/common/Spinner';
import { DeleteConfirmationModal } from '../components/common/DeleteConfirmationModal';
import { FiUpload, FiSettings, FiUser, FiTrash2 } from 'react-icons/fi';
import { useToast } from '../contexts/ToastContext';
import { PersonaMarkdown } from '../components/personas/PersonaMarkdown';
import { getPersonaQualityTier } from '../utils/persona_quality';
import { TIER_PRESENTATION, getTierPresentation } from '../utils/personaTier';
import { usePersonas } from '../hooks/usePersonas';

// Define categories based on common tags
const CATEGORIES = [
  { id: 'committee', name: 'Committee', description: 'Personas for structured deliberation and decision-making' },
  { id: 'technical', name: 'Technical', description: 'Development, coding, and engineering personas' },
  { id: 'devops', name: 'DevOps & SRE', description: 'Infrastructure, operations, and reliability personas' },
  { id: 'data-ai', name: 'Data & AI', description: 'Data engineering, machine learning, and AI personas' },
  { id: 'creative', name: 'Creative', description: 'Content creation, design, and documentation personas' },
  { id: 'product', name: 'Product', description: 'Product management, UX research, and strategy personas' },
  { id: 'critical-thinker', name: 'Critical Thinking', description: 'Personas that challenge assumptions and provide critical analysis' }
];

// Map tags to categories
const TAG_TO_CATEGORY: Record<string, string> = {
  // Committee personas
  'committee': 'committee',
  'orchestration': 'committee',
  'facilitation': 'committee',
  'moderation': 'committee',
  'governance': 'committee',
  'agenda': 'committee',
  
  // Technical personas
  'fullstack': 'technical',
  'frontend': 'technical',
  'backend': 'technical',
  'api': 'technical',
  'python': 'technical',
  'code-quality': 'technical',
  'refactoring': 'technical',
  
  // DevOps personas
  'devops': 'devops',
  'sre': 'devops',
  'linux': 'devops',
  'docker': 'devops',
  'observability': 'devops',
  'security': 'devops',
  
  // Data & AI personas
  'data': 'data-ai',
  'ml': 'data-ai',
  'dl': 'data-ai',
  'etl': 'data-ai',
  'llm': 'data-ai',
  'rag': 'data-ai',
  
  // Creative personas
  'creative': 'creative',
  'documentation': 'creative',
  'brand': 'creative',
  'visual': 'creative',
  'storytelling': 'creative',
  
  // Product personas
  'product': 'product',
  'ux': 'product',
  'research': 'product',
  'roadmap': 'product',
  'prioritization': 'product',
  
  // Critical thinking personas
  'critical-thinker': 'critical-thinker',
  'first-principles': 'critical-thinker',
  'second-order-thinking': 'critical-thinker',
  'critique': 'critical-thinker',
  'validation': 'critical-thinker'
};

// Format date helper
const formatDate = (dateString?: string) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

import { migrateCapabilities } from '../utils/persona_engine_utils';
import { MigrateToForgeButton } from '../components/personas/MigrateToForgeButton';

export const Personas: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedPersona, setSelectedPersona] = useState<Persona | null>(null);
  const [filteredPersonas, setFilteredPersonas] = useState<Persona[]>([]);
  const [allTags, setAllTags] = useState<{ name: string; count: number }[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortField, setSortField] = useState<string>('updated_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [capabilitySortField, setCapabilitySortField] = useState<'count' | 'avg'>('count');
  const [showAllCapabilities, setShowAllCapabilities] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [personaToDelete, setPersonaToDelete] = useState<Persona | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const canDeletePersona = !!user?.is_superuser;

  const handleDeleteRequest = (persona: Persona) => {
    setPersonaToDelete(persona);
  };

  const handleDeleteConfirm = async () => {
    if (!personaToDelete) return;
    setIsDeleting(true);
    try {
      await personaService.deletePersona(personaToDelete.id);
      await queryClient.invalidateQueries(['personas']);
      await queryClient.invalidateQueries(['personaTags']);
      showToast(`Persona "${personaToDelete.name}" was deleted`, 'success');
      setPersonaToDelete(null);
    } catch (err) {
      console.error('Error deleting persona:', err);
      showToast('Could not delete persona. You may not have permission.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Fetch all personas via the shared hook (refetch preserved for post-import refresh)
  const { data: personas, isLoading, error, refetch } = usePersonas();
  
  // Add these functions for import
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsImporting(true);
      
      const results = await personaService.importPersonas(file);

      showToast(results.message, 'success');

      // Refresh the personas list
      refetch();
    } catch (error) {
      console.error('Import error:', error);
      showToast('There was an error importing the personas', 'error');
    } finally {
      setIsImporting(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  // Fetch all tags with counts
  const { data: tagData } = useQuery(
    ['personaTags'],
    () => personaService.getPersonaTags()
  );
  
  // Calculate capability distribution - more detailed analysis
  const capabilityDistribution = useMemo(() => {
    if (!personas) return {};
    
    const distribution: Record<string, {count: number, avgValue: number, maxValue: number}> = {};
    
    personas?.forEach(persona => {
      if (persona.capabilities) {
        Object.entries(persona.capabilities).forEach(([key, value]) => {
          if (!distribution[key]) {
            distribution[key] = { count: 0, avgValue: 0, maxValue: 0 };
          }
          distribution[key].count += 1;
          distribution[key].avgValue += value;
          distribution[key].maxValue = Math.max(distribution[key].maxValue, value);
        });
      }
    });
    
    // Calculate averages
    Object.keys(distribution).forEach(key => {
      if (distribution[key].count > 0) {
        distribution[key].avgValue = Math.round(distribution[key].avgValue / distribution[key].count);
      }
    });
    
    return distribution;
  }, [personas]);

  // Get max capability count for scaling
  const maxCapability = useMemo(() => {
    if (Object.keys(capabilityDistribution).length === 0) return 1;
    return Math.max(...Object.values(capabilityDistribution).map(v => v.count));
  }, [capabilityDistribution]);

  // Get top persona by usage
  const topPersona = useMemo(() => {
    if (!personas) return null;
    return [...personas].sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0))[0];
  }, [personas]);

  // Get most recently added persona
  const recentPersona = useMemo(() => {
    if (!personas) return null;
    return [...personas].sort((a, b) => {
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return dateB.getTime() - dateA.getTime();
    })[0];
  }, [personas]);

  // ── Forge quality tier derivation ──────────────────────────────────────────
  const forgeQualityTiers = useMemo(() => {
    if (!personas) return { forged: 0, draft: 0, bare: 0 };
    let forged = 0, draft = 0, bare = 0;
    personas.forEach(p => {
      const tier = getPersonaQualityTier(p);
      if (tier === 'forged') forged++;
      else if (tier === 'draft') draft++;
      else bare++;
    });
    return { forged, draft, bare };
  }, [personas]);

  // ── Forge pipeline funnel derivation ───────────────────────────────────────
  const forgePipeline = useMemo(() => {
    if (!personas) return { total: 0, hasBible: 0, compiled: 0, probed: 0 };
    const total = personas.length;
    let hasBible = 0, compiled = 0, probed = 0;
    personas.forEach(p => {
      const pa = p as any;
      const forge =
        pa?.data?.engineering_profile?.current_state
        ?? pa?.data?.data?.engineering_profile?.current_state
        ?? null;
      if (forge?.character_bible) hasBible++;
      if (forge?.system_prompt) compiled++;
      if (forge?.probes?.length > 0) probed++;
    });
    return { total, hasBible, compiled, probed };
  }, [personas]);

  // ── Activity tab data ──────────────────────────────────────────────────────
  const recentlyActivePersonas = useMemo(() => {
    if (!personas) return [];
    return [...personas]
      .sort((a, b) => {
        const dateA = new Date((a as any).updated_at || a.updatedAt || (a as any).created_at || a.createdAt || 0).getTime();
        const dateB = new Date((b as any).updated_at || b.updatedAt || (b as any).created_at || b.createdAt || 0).getTime();
        return dateB - dateA;
      })
      .slice(0, 7);
  }, [personas]);

  // getPersonaQualityTier lifted to ../utils/persona_quality (Trailhead v0.1)

  const qualityDot = (tier: 'forged' | 'draft' | 'bare') => (
    <span className={`inline-block w-2 h-2 rounded-full mr-1.5 flex-shrink-0 ${TIER_PRESENTATION[tier].dotClass}`} />
  );

  // ── Capability avg for list view sort ─────────────────────────────────────
  const getPersonaCapAvg = (p: Persona): number => {
    const pa = p as any;
    const rawCaps =
      pa?.data?.engineering_profile?.current_state?.capabilities
      ?? pa?.data?.data?.engineering_profile?.current_state?.capabilities
      ?? p.capabilities
      ?? {};
    const entries = Object.values(migrateCapabilities(rawCaps));
    if (!entries.length) return 0;
    return entries.reduce((sum, e) => sum + e.score, 0) / entries.length;
  };
  
  // Update tags when tag data changes
  useEffect(() => {
    if (tagData) {
      setAllTags(tagData);
    } else if (personas) {
      // Fallback to client-side extraction if API fails
      const tagCounts: Record<string, number> = {};
      
      personas.forEach(persona => {
        if (persona.tags) {
          persona.tags.forEach(tag => {
            tagCounts[tag] = (tagCounts[tag] || 0) + 1;
          });
        }
      });
      
      const tagsArray = Object.entries(tagCounts).map(([name, count]) => ({ name, count }));
      tagsArray.sort((a, b) => b.count - a.count); // Sort by count descending
      
      setAllTags(tagsArray);
    }
  }, [tagData, personas]);
  
  // Filter personas based on search, category, and tags
  useEffect(() => {
    if (!personas) return;
    
    let filtered = [...personas];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(persona => 
        persona.name.toLowerCase().includes(query) || 
        persona.prompt?.toLowerCase().includes(query) ||
        persona.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(persona => 
        persona.tags?.some(tag => TAG_TO_CATEGORY[tag] === selectedCategory)
      );
    }
    
    // Apply tag filters
    if (selectedTags.length > 0) {
      filtered = filtered.filter(persona => 
        selectedTags.every(tag => persona.tags?.includes(tag))
      );
    }
    
    setFilteredPersonas(filtered);
  }, [personas, searchQuery, selectedCategory, selectedTags]);
  
  // Sort personas
  const sortedPersonas = useMemo(() => {
    if (!filteredPersonas) return [];
    
    return [...filteredPersonas].sort((a, b) => {
      // Quality sort: Forged > Draft > Bare
      if (sortField === 'quality') {
        const order = { forged: 2, draft: 1, bare: 0 };
        const aVal = order[getPersonaQualityTier(a)];
        const bVal = order[getPersonaQualityTier(b)];
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      // Capabilities avg sort
      if (sortField === 'cap_avg') {
        const aVal = getPersonaCapAvg(a);
        const bVal = getPersonaCapAvg(b);
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      // Handle date fields specifically
      if (sortField === 'updated_at' || sortField === 'created_at') {
        // Handle both field naming conventions
        const aDate = new Date(a[sortField] || a.updatedAt || a.createdAt || 0).getTime();
        const bDate = new Date(b[sortField] || b.updatedAt || b.createdAt || 0).getTime();
        return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
      }
      
      // Handle nested properties like 'capabilities.creativity'
      let aValue, bValue;
      
      if (sortField.includes('.')) {
        // Handle nested properties
        const parts = sortField.split('.');
        let aProp = a as any;
        let bProp = b as any;
        
        for (const part of parts) {
          aProp = aProp && typeof aProp === 'object' ? aProp[part] : undefined;
          bProp = bProp && typeof bProp === 'object' ? bProp[part] : undefined;
        }
        
        aValue = aProp;
        bValue = bProp;
      } else {
        // Handle regular properties
        aValue = (a as any)[sortField];
        bValue = (b as any)[sortField];
      }
      
      // Handle undefined or null values
      if (aValue === undefined || aValue === null) return sortDirection === 'asc' ? -1 : 1;
      if (bValue === undefined || bValue === null) return sortDirection === 'asc' ? 1 : -1;
      
      // Handle numeric fields
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // Handle string fields
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue) 
          : bValue.localeCompare(aValue);
      }
      
      return 0;
    });
  }, [filteredPersonas, sortField, sortDirection]);
  
  // Handle tag selection
  const handleTagSelect = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag) // Remove tag if already selected
        : [...prev, tag] // Add tag if not selected
    );
  };
  
  // Handle category selection
  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
    
    // Clear tag selection if it doesn't belong to the selected category
    if (categoryId !== 'all') {
      setSelectedTags(prev => 
        prev.filter(tag => TAG_TO_CATEGORY[tag] === categoryId)
      );
    }
  };
  
  // Handle persona selection for quick view
  const handleViewDetails = (personaId: string) => {
    const persona = personas?.find(p => p.id === personaId) || null;
    setSelectedPersona(persona);
  };
  
  // Handle tag click from persona detail
  const handleTagClick = (tag: string) => {
    setSelectedPersona(null); // Close detail view
    handleTagSelect(tag); // Select the tag
  };
  
  // Handle sort click
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Render sort indicator
  const renderSortIndicator = (field: string) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };
  
  
  // Render list view
  const renderListView = () => {
    return (
      <div className={`overflow-x-auto rounded-lg border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <table className={`w-full divide-y ${darkMode ? 'divide-gray-700 bg-gray-800 text-gray-200' : 'divide-gray-200 bg-white text-gray-800'}`}>
          <thead className={darkMode ? 'bg-gray-900' : 'bg-gray-50'}>
            <tr>
              <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider">
                Actions
              </th>
              <th
                className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer w-56"
                onClick={() => handleSort('name')}
              >
                Name{renderSortIndicator('name')}
              </th>
              <th
                className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('primarySpecialization')}
              >
                Specialization{renderSortIndicator('primarySpecialization')}
              </th>
              <th
                className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('quality')}
              >
                Quality{renderSortIndicator('quality')}
              </th>
              <th
                className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('cap_avg')}
              >
                Capabilities{renderSortIndicator('cap_avg')}
              </th>
              <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider">
                Tags
              </th>
              <th
                className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('updated_at')}
              >
                Last Updated{renderSortIndicator('updated_at')}
              </th>
              <th
                className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('usageCount')}
              >
                Usage{renderSortIndicator('usageCount')}
              </th>
            </tr>
          </thead>
          <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
            {sortedPersonas.map((persona) => {
              const tier = getPersonaQualityTier(persona);
              const capAvg = getPersonaCapAvg(persona);
              const hasEngProfile = tier !== 'bare';
              return (
                <tr
                  key={persona.id}
                  className={`transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                >
                  {/* Actions */}
                  <td className="px-2 py-2 whitespace-nowrap text-xs">
                    <div className="flex items-center gap-1 flex-wrap">
                      <button
                        onClick={() => handleViewDetails(persona.id)}
                        className={`text-xs px-2 py-0.5 rounded ${
                          darkMode ? 'bg-gray-700 hover:bg-gray-600 text-blue-300' : 'bg-gray-100 hover:bg-gray-200 text-blue-600'
                        }`}
                      >
                        View
                      </button>
                      <Link
                        to={`/personas/${persona.id}`}
                        className={`text-xs px-2 py-0.5 rounded ${
                          darkMode ? 'bg-gray-700 hover:bg-gray-600 text-green-300' : 'bg-gray-100 hover:bg-gray-200 text-green-600'
                        }`}
                      >
                        Edit
                      </Link>
                      <Link
                        to={`/personas/forge?personaId=${persona.id}`}
                        className={`text-xs px-2 py-0.5 rounded ${
                          darkMode ? 'bg-gray-700 hover:bg-gray-600 text-purple-300' : 'bg-gray-100 hover:bg-gray-200 text-purple-600'
                        }`}
                      >
                        Forge
                      </Link>
                      {!hasEngProfile && (
                        <MigrateToForgeButton
                          personaId={persona.id}
                          hasEngineeringProfile={false}
                          compact={true}
                        />
                      )}
                    </div>
                  </td>
                  {/* Name (with right-edge Delete) */}
                  <td className="px-2 py-2 whitespace-nowrap text-xs font-medium max-w-[220px]">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{persona.name}</span>
                      {canDeletePersona && (
                        <button
                          onClick={() => handleDeleteRequest(persona)}
                          title="Delete persona"
                          aria-label="Delete persona"
                          className={`flex-shrink-0 p-1 rounded transition-colors ${
                            darkMode
                              ? 'text-red-700 hover:text-red-400 hover:bg-red-900/30'
                              : 'text-red-800 hover:text-red-600 hover:bg-red-50'
                          }`}
                        >
                          <FiTrash2 size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                  {/* Specialization */}
                  <td className="px-2 py-2 whitespace-nowrap text-xs">
                    {persona.primarySpecialization || 'General'}
                  </td>
                  {/* Quality */}
                  <td className="px-2 py-2 whitespace-nowrap text-xs">
                    {(() => {
                      const tp = getTierPresentation(persona);
                      return (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tp.chipClass}`}>
                          <span aria-hidden="true">{tp.glyph}</span>&nbsp;{tp.label}
                        </span>
                      );
                    })()}
                  </td>
                  {/* Capabilities avg bar */}
                  <td className="px-2 py-2 whitespace-nowrap text-xs">
                    {capAvg > 0 ? (
                      <div className="flex items-center gap-2">
                        <div className={`w-20 h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                          <div
                            className="h-2 rounded-full bg-cyan-500"
                            style={{ width: `${(capAvg / 10) * 100}%` }}
                          />
                        </div>
                        <span className="tabular-nums">{capAvg.toFixed(1)}</span>
                      </div>
                    ) : (
                      <span className={darkMode ? 'text-gray-600' : 'text-gray-400'}>—</span>
                    )}
                  </td>
                  {/* Tags */}
                  <td className="px-2 py-2 whitespace-nowrap text-xs">
                    <div className="flex flex-wrap gap-1">
                      {persona.tags?.slice(0, 3).map(tag => (
                        <span
                          key={tag}
                          className={`text-xs px-1 py-0.5 rounded ${
                            darkMode ? 'bg-blue-900 text-blue-100' : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {tag}
                        </span>
                      ))}
                      {persona.tags && persona.tags.length > 3 && (
                        <span className="text-xs text-gray-500">+{persona.tags.length - 3} more</span>
                      )}
                    </div>
                  </td>
                  {/* Last Updated */}
                  <td className="px-2 py-2 whitespace-nowrap text-xs">
                    {new Date((persona as any).updated_at || persona.updatedAt || '').toLocaleDateString()}
                  </td>
                  {/* Usage */}
                  <td className="px-2 py-2 whitespace-nowrap text-xs">
                    {persona.usageCount || 0}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };
  

  return (
    <div className="transition-colors duration-200">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
        <div className={`w-full flex flex-col md:flex-row justify-between items-start md:items-center gap-3 rounded-lg shadow-lg border-l-4 px-4 py-3 ${
          darkMode ? 'bg-gray-800 border-purple-600' : 'bg-white border-purple-500 shadow-sm'
        }`}>
          <div>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${darkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
                <FiUser className={`h-5 w-5 ${darkMode ? 'text-purple-300' : 'text-purple-700'}`} />
              </div>
              <div>
                <h1 className={`text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r ${
                  darkMode ? 'from-purple-400 to-violet-400' : 'from-purple-600 to-violet-600'
                }`}>
                  Personas
                </h1>
                <p className={`text-xs italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Your fleet of specialized AI collaborators
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 shrink-0">
            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".json,.jsonl" className="hidden" />
            <button
              onClick={() => navigate('/personas/iomgr')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
              }`}
            >
              <FiSettings className="w-3.5 h-3.5" /> I/O Manager
            </button>
            <Link
              to="/personas/forge?new=true"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-colors bg-gradient-to-r ${
                darkMode ? 'from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700' : 'from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600'
              }`}
            >
              <FiUser className="w-3.5 h-3.5" /> Forge New
            </Link>
        </div>
        </div>
      </div>

      <div className="h-px mb-4 bg-gradient-to-r from-transparent via-purple-500/20 to-transparent" />

      {/* Dashboard stats — 3 focused signal cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">

        {/* Card A — Fleet Health */}
        <div className={`p-4 rounded-lg shadow-lg border-l-4 ${darkMode ? 'bg-gray-800 border-purple-600' : 'bg-white border-purple-500 shadow-sm'}`}>
          <h2 className="text-sm font-semibold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-violet-400">Fleet Health</h2>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-3xl font-bold">{personas?.length || 0}</span>
            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>personas</span>
          </div>
          {/* Quality tier pills */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className={`flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${TIER_PRESENTATION.forged.chipClass}`}>
              <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${TIER_PRESENTATION.forged.dotClass}`} />
              Forged {forgeQualityTiers.forged}
            </span>
            <span className={`flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${TIER_PRESENTATION.draft.chipClass}`}>
              <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${TIER_PRESENTATION.draft.dotClass}`} />
              Draft {forgeQualityTiers.draft}
            </span>
            <span className={`flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${TIER_PRESENTATION.bare.chipClass}`}>
              <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${TIER_PRESENTATION.bare.dotClass}`} />
              Bare {forgeQualityTiers.bare}
            </span>
          </div>
          {/* Segmented progress bar */}
          {(personas?.length || 0) > 0 && (
            <div className="flex h-2 rounded-full overflow-hidden gap-px">
              {forgeQualityTiers.forged > 0 && (
                <div
                  className={`${TIER_PRESENTATION.forged.dotClass} transition-all`}
                  style={{ width: `${(forgeQualityTiers.forged / (personas?.length || 1)) * 100}%` }}
                  title={`Forged: ${forgeQualityTiers.forged}`}
                />
              )}
              {forgeQualityTiers.draft > 0 && (
                <div
                  className={`${TIER_PRESENTATION.draft.dotClass} transition-all`}
                  style={{ width: `${(forgeQualityTiers.draft / (personas?.length || 1)) * 100}%` }}
                  title={`Draft: ${forgeQualityTiers.draft}`}
                />
              )}
              {forgeQualityTiers.bare > 0 && (
                <div
                  className={`${TIER_PRESENTATION.bare.dotClass} transition-all`}
                  style={{ width: `${(forgeQualityTiers.bare / (personas?.length || 1)) * 100}%` }}
                  title={`Bare: ${forgeQualityTiers.bare}`}
                />
              )}
            </div>
          )}
        </div>

        {/* Card B — Recent Activity */}
        <div className={`p-4 rounded-lg shadow-lg border-l-4 ${darkMode ? 'bg-gray-800 border-purple-600' : 'bg-white border-purple-500 shadow-sm'}`}>
          <h2 className="text-sm font-semibold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-violet-400">Recent Activity</h2>
          <div className="space-y-1.5">
            {recentlyActivePersonas.map(p => {
              const tier = getPersonaQualityTier(p);
              const updatedAt = (p as any).updated_at || p.updatedAt || (p as any).created_at || p.createdAt;
              return (
                <div
                  key={p.id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                  onClick={() => handleViewDetails(p.id)}
                >
                  {qualityDot(tier)}
                  <span className="text-xs flex-1 truncate">{p.name}</span>
                  <span className={`text-xs flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {formatDate(updatedAt)}
                  </span>
                </div>
              );
            })}
            {recentlyActivePersonas.length === 0 && (
              <div className={`text-xs text-center py-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No data</div>
            )}
          </div>
        </div>

        {/* Card C — Forge Pipeline Funnel */}
        <div className={`p-4 rounded-lg shadow-lg border-l-4 ${darkMode ? 'bg-gray-800 border-purple-600' : 'bg-white border-purple-500 shadow-sm'}`}>
          <h2 className="text-sm font-semibold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-violet-400">Forge Pipeline</h2>
          {(() => {
            const steps = [
              { label: 'Total',    count: forgePipeline.total,    prev: forgePipeline.total,    color: 'bg-gray-400',  hex: '#9ca3af' },
              { label: 'Authored', count: forgePipeline.hasBible, prev: forgePipeline.total,    color: 'bg-blue-500',  hex: '#3b82f6' },
              { label: 'Compiled', count: forgePipeline.compiled, prev: forgePipeline.hasBible, color: 'bg-green-500', hex: '#22c55e' },
              { label: 'Probed',   count: forgePipeline.probed,   prev: forgePipeline.compiled, color: 'bg-amber-500', hex: '#f59e0b' },
            ];

            // Find the worst drop-off step (biggest absolute loss, skip Total)
            let worstIdx = -1;
            let worstDrop = 0;
            steps.slice(1).forEach((step, i) => {
              const drop = step.prev - step.count;
              if (drop > worstDrop) { worstDrop = drop; worstIdx = i + 1; }
            });

            return (
              <div className="space-y-1">
                {steps.map(({ label, count, prev, color, hex }, idx) => {
                  const pct = prev > 0 ? Math.round((count / prev) * 100) : 0;
                  // Bar width = drop-off percentage of its own previous step
                  const barWidth = idx === 0
                    ? 100
                    : prev > 0 ? (count / prev) * 100 : 0;
                  const isWorst = idx === worstIdx && worstDrop > 0;

                  return (
                    <React.Fragment key={label}>
                      {/* Arrow connector between steps */}
                      {idx > 0 && (
                        <div className={`flex items-center gap-1 py-0.5 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>
                          <div className={`flex-1 border-t border-dashed ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} />
                          <span className="text-xs">↓</span>
                          <div className={`flex-1 border-t border-dashed ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} />
                        </div>
                      )}

                      <div>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {label}
                          </span>
                          <span className={`text-xs font-semibold tabular-nums ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            {count}
                            {idx > 0 && (
                              <span className={`ml-1 font-normal ${
                                pct < 50
                                  ? darkMode ? 'text-red-400' : 'text-red-500'
                                  : pct < 80
                                    ? darkMode ? 'text-amber-400' : 'text-amber-600'
                                    : darkMode ? 'text-gray-500' : 'text-gray-400'
                              }`}>
                                ({pct}%)
                              </span>
                            )}
                          </span>
                        </div>
                        <div className={`h-1.5 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                          <div
                            className={`h-1.5 rounded-full transition-all ${color}`}
                            style={{ width: `${barWidth}%` }}
                          />
                        </div>
                        {/* Worst drop-off nudge */}
                        {isWorst && (
                          <p className={`text-xs mt-1 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                            ⚠ {worstDrop} stuck here — open in Forge to continue
                          </p>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })()}
          <div className="mt-3 pt-2 border-t border-gray-700/30">
            {forgePipeline.total > 0 && forgePipeline.probed === forgePipeline.total ? (
              <span className={`text-xs font-medium ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                ✓ Fleet complete
              </span>
            ) : forgePipeline.total === 0 ? (
              <button
                onClick={() => navigate('/personas/forge?new=true')}
                className={`text-xs font-medium ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
              >
                Forge your first persona →
              </button>
            ) : (
              <button
                onClick={() => navigate('/personas/forge?new=true')}
                className={`text-xs font-medium ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
              >
                Forge New →
              </button>
            )}
          </div>
        </div>

      </div>
      
      <CategoryTabs 
        categories={CATEGORIES}
        selectedCategory={selectedCategory}
        onCategorySelect={handleCategorySelect}
      />
      
      <TagFilter 
        tags={allTags}
        selectedTags={selectedTags}
        onTagSelect={handleTagSelect}
        personas={personas || []}
      />
      
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-baseline gap-2">
          <h2 className="text-sm font-semibold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-violet-400">
            Available Personas
          </h2>
          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {filteredPersonas.length} of {personas?.length || 0}
          </span>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Search input moved here with max-width */}
          <div className="relative" style={{ maxWidth: '200px' }}>
            <input
              type="text"
              placeholder="Search personas..."
              className={`w-full px-3 py-1 text-sm border ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              } rounded-md transition-colors duration-200`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className={`absolute right-2 top-1/2 transform -translate-y-1/2 ${
                  darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setSearchQuery('')}
                title="Clear search"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
          {/* Sort dropdown */}
          <div className="relative">
            <select
              value={`${sortField}-${sortDirection}`}
              onChange={(e) => {
                const [field, direction] = e.target.value.split('-');
                setSortField(field);
                setSortDirection(direction as 'asc' | 'desc');
              }}
              className={`px-3 py-1 text-sm border ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              } rounded-md transition-colors duration-200`}
            >
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="updated_at-desc">Newest First</option>
              <option value="updated_at-asc">Oldest First</option>
              <option value="usageCount-desc">Most Used</option>
            </select>
          </div>
          
          {/* View toggle buttons */}
          <div className="flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`px-4 py-2 text-sm font-medium ${
                viewMode === 'grid'
                  ? (darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                  : (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700')
              } ${viewMode === 'grid' ? '' : 'hover:bg-gray-300'} rounded-l-lg`}
            >
              Grid
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 text-sm font-medium ${
                viewMode === 'list'
                  ? (darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                  : (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700')
              } ${viewMode === 'list' ? '' : 'hover:bg-gray-300'} rounded-r-lg`}
            >
              List
            </button>
          </div>
          
        </div>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center p-8"><Spinner size="lg" /></div>
      ) : error ? (
        <div className="text-center py-10 text-red-600">Error loading personas</div>
      ) : sortedPersonas.length === 0 && (searchQuery || selectedCategory !== 'all' || selectedTags.length > 0) ? (
        <div className={`flex flex-col items-center justify-center py-16 rounded-lg border border-dashed ${darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'}`}>
          <p className="text-sm font-medium mb-3">No personas match your current filters.</p>
          <button
            onClick={() => {
              setSearchQuery('');
              setSelectedCategory('all');
              setSelectedTags([]);
            }}
            className={`text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
          >
            Clear all filters
          </button>
        </div>
      ) : (
        viewMode === 'grid' ? (
          <PersonaGrid
            personas={sortedPersonas}
            onViewDetails={handleViewDetails}
            onDelete={canDeletePersona ? handleDeleteRequest : undefined}
          />
        ) : (
          renderListView()
        )
      )}
      
      
      {/* All capabilities modal */}
      {showAllCapabilities && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-lg p-6 max-w-2xl w-full ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">All Capabilities ({Object.keys(capabilityDistribution).length})</h3>
              <button
                onClick={() => setShowAllCapabilities(false)}
                className={`p-1 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            
            <div className="flex space-x-2 mb-4">
              <button 
                onClick={() => setCapabilitySortField('count')}
                className={`px-3 py-1 text-sm rounded ${
                  capabilitySortField === 'count' 
                    ? (darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white') 
                    : (darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700')
                }`}
              >
                Sort by Count
              </button>
              <button 
                onClick={() => setCapabilitySortField('avg')}
                className={`px-3 py-1 text-sm rounded ${
                  capabilitySortField === 'avg' 
                    ? (darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white') 
                    : (darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700')
                }`}
              >
                Sort by Average Value
              </button>
            </div>
            
            <div className="overflow-y-auto max-h-96">
              <table className={`w-full ${darkMode ? 'text-gray-300' : 'text-gray-700'} border-collapse`}>
                <thead className="sticky top-0 z-10">
                  <tr className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b`}>
                    <th className="text-left py-2 px-3 text-sm font-medium">Capability</th>
                    <th className="text-right py-2 px-3 text-sm font-medium">Count</th>
                    <th className="text-right py-2 px-3 text-sm font-medium">Avg Value</th>
                    <th className="text-right py-2 px-3 text-sm font-medium">Max Value</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(capabilityDistribution)
                    .sort((a, b) => 
                      capabilitySortField === 'count' 
                        ? b[1].count - a[1].count 
                        : b[1].avgValue - a[1].avgValue
                    )
                    .map(([key, data]) => (
                      <tr key={key} className={`${darkMode ? 'border-gray-700' : 'border-gray-200'} border-b`}>
                        <td className="py-2 px-3 capitalize">
                          <div className="flex items-center">
                            <div 
                              className={`w-3 h-3 rounded-full mr-2 ${getCapabilityColor(key)}`}
                            ></div>
                            {key}
                          </div>
                        </td>
                        <td className="text-right py-2 px-3">{data.count}</td>
                        <td className="text-right py-2 px-3">
                          <div className="flex items-center justify-end">
                            <span className="mr-2">{data.avgValue.toFixed(1)}</span>
                            <div className="w-24 bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                              <div 
                                className={`h-2 rounded-full ${getCapabilityColor(key)}`} 
                                style={{ width: `${data.avgValue * 10}%` }}
                              ></div>
                            </div>
                          </div>
                        </td>
                        <td className="text-right py-2 px-3">{data.maxValue.toFixed(1)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {selectedPersona && (
        <PersonaDetail
          persona={selectedPersona}
          onClose={() => setSelectedPersona(null)}
          onTagClick={handleTagClick}
          onUsePersona={(personaId) => {
            // Handle using the persona
            console.log(`Using persona: ${personaId}`);
            setSelectedPersona(null);
          }}
        />
      )}

      <DeleteConfirmationModal
        isOpen={!!personaToDelete}
        onClose={() => !isDeleting && setPersonaToDelete(null)}
        onConfirm={handleDeleteConfirm}
        itemType="persona"
        itemName={personaToDelete?.name}
        isDeleting={isDeleting}
        dangerLevel="high"
      />
    </div>
  );
};
